import React, { ReactElement, useEffect, useMemo, useRef } from "react";
import AreaReadDto from "../../../Models/Area/AreaReadDto";
import { MachineReadDto } from "../../../Models/Machine/MachinesReadDto";
import { ConsumableReadDto } from "../../../Models/Consumable/ConsumableReadDto";
import { ScheduleReadDto } from "../../../Models/Schedule/ScheduleReadDto";
import { PointReadTableDto } from "../../../Models/Point/PointReadTableDto";
import { ColumnMetaData } from "../../Shared/Interfaces/ColumnMetaData";
import GBKendoDataCell from "../../Shared/Components/Table/GBKendoDataCell";
import useKendoTableState from "../../Shared/Hooks/useKendoTableState";
import PointValidationSchema from "../Utilities/PointValidationSchema";
import { Formik, FormikProps } from "formik";
import { Button } from "@progress/kendo-react-buttons";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridPageChangeEvent, GridToolbar, GridColumn as Column, GridFilterChangeEvent, GridSortChangeEvent, GridRowProps } from "@progress/kendo-react-grid";
import { isRequiredBySchema } from "../../Shared/Utilities/YupValidationUtilities";
import GBKendoToolbar from "../../Shared/Components/Table/GBKendoToolbar";
import { Checkbox } from "@progress/kendo-react-inputs";
import { CustomDateFilterCell } from "../../Shared/Components/Table/CustomDateFilterCell";
import { CustomTextFilterCell } from "../../Shared/Components/Table/CustomTextFilterCell";
import { CompositeFilterDescriptor, filterBy, orderBy } from "@progress/kendo-data-query";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../messages/en-US";
import { useToast } from "@chakra-ui/react";
import pointsApiService from "../Utilities/PointsApiService";
import EntityNotesButton from "../../Dashboards/Assets/Components/Notes/EntityNotesButton";

interface Props {
  points: Array<PointReadTableDto>;
  schedules: Array<ScheduleReadDto>;
  areas: Array<AreaReadDto>;
  machines: Array<MachineReadDto>;
  consumables: Array<ConsumableReadDto>;
  createPoint: (values: any, setStatus: any) => void;
  deletePoints: (deleteIds: Array<string>) => void;
  updatePoints: (points: Array<PointReadTableDto>) => void;
  height: number;
}

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
}


function PointTable(props: any) {
  const PURGE_POINT = -1;

  const localizationService = useLocalization();

  const toast = useToast({
    position: "bottom",
    duration: 5000,
    isClosable: true,
  });

  const tableState = useKendoTableState<PointReadTableDto>({
    key: "Points",
    initialAssets: props.points,
  });

  const stateRef = useRef(tableState);

  useEffect(() => {
    stateRef.current = tableState
   }, [tableState]);
  
  const [filter, setFilter] = React.useState(initialFilter);
  const [sort, setSort] = React.useState(props.initialSortDescriptor ?? props.initialSort);
  const [previousSort, setPreviousSort] = React.useState(sort || props.initialSortDescriptor || props.initialSort);
  const [editModeRows, setEditModeRows] = React.useState<Array<any>>([]);
 
  // Table columns
  const columns: Array<ColumnMetaData<PointReadTableDto>> = React.useMemo(
      () =>
        [
          {
            header: `${localizationService.toLanguageString('custom.notes', enMessages.custom.notes)}`,
            displayField: "description",
            filterType: "boolean",
            Cell: (cellProps) => (
              <GBKendoDataCell<PointReadTableDto>
                className={"text-center"}
                dataFieldName="description"
                cellProps={cellProps}
                fieldAccessor={(x) => (
                  <EntityNotesButton
                    entityType="Point"
                    entityId={x.id}
                    entityName={x.name}
                    entitySerial={x.currentTagRfid}
                    buttonProps={{ fillMode: "flat" }}
                  />
                )}
                isAddMode={false}
                isEditMode={false}
                onCellChanged={tableState.onCellChanged}
              />
            ),
          },
          {
            header: `${localizationService.toLanguageString('custom.order', enMessages.custom.order)}`,
            displayField: "routeOrder",
            Cell: (cellProps) => (
              <GBKendoDataCell<PointReadTableDto>
                dataFieldName="routeOrder"
                cellProps={cellProps}
                fieldAccessor={(x) => x.routeOrder}
                isAddMode={cellProps.dataIndex < tableState.tempRows.length}
                isEditMode={false}
                onCellChanged={tableState.onCellChanged}
              />
            ),
          },
          {
            header: `${localizationService.toLanguageString('custom.name', enMessages.custom.name)}`,
            displayField: "name",
            filterType: "text",
            Cell: (cellProps) => (
              <GBKendoDataCell<PointReadTableDto>
                dataFieldName="name"
                cellProps={cellProps}
                fieldAccessor={(x) => x.name}
                isAddMode={cellProps.dataIndex < tableState.tempRows.length}
                isEditMode={tableState.canModifyRow}
                onCellChanged={tableState.onCellChanged}
              />
            ),
          },
          {
            header: `${localizationService.toLanguageString('custom.asset', enMessages.custom.asset)}`,
            displayField: "machineName",
            Cell: (cellProps) => (
              <GBKendoDataCell<PointReadTableDto>
                dataFieldName="machineId"
                fieldAccessor={(x) => x.machineId}
                displayTextAccessor={(x) => x.name}
                cellProps={cellProps}
                isAddMode={cellProps.dataIndex < tableState.tempRows.length}
                isEditMode={tableState.canModifyRow}
                onCellChanged={tableState.onCellChanged}
                assets={props.machines.filter(
                  (x: { areaId: string; }) =>
                    cellProps.dataItem.areaId == null ||
                    cellProps.dataItem.areaId === "" ||
                    x.areaId === cellProps.dataItem.areaId
                )} //Only show valid machines if area selected
                inputType="combobox"
                initialDisplayText={cellProps.dataItem.machineName}
                onChange={(newValue, form) => onMachineSelected(cellProps.dataItem.id, newValue, form)}
              />
            )
          },
          {
            header: `${localizationService.toLanguageString('custom.location', enMessages.custom.location)}`,
            displayField: "areaName",
            Cell: (cellProps) => (
              <GBKendoDataCell<PointReadTableDto>
                dataFieldName="areaId"
                fieldAccessor={(x) => x.areaId}
                displayTextAccessor={(x) => x.name}
                cellProps={cellProps}
                isAddMode={cellProps.dataIndex < tableState.tempRows.length}
                isEditMode={tableState.canModifyRow}
                onCellChanged={tableState.onCellChanged}
                assets={props.areas}
                inputType="combobox"
                initialDisplayText={cellProps.dataItem.areaName}
              />
            ),
          },
          {
            header: `${localizationService.toLanguageString('custom.consumable', enMessages.custom.consumable)}`,
            displayField: "consumableName",
            Cell: (cellProps) => (
              <GBKendoDataCell<PointReadTableDto>
                dataFieldName="consumableId"
                fieldAccessor={(x) => x.consumableId}
                displayTextAccessor={(x) => x.name}
                cellProps={cellProps}
                isAddMode={cellProps.dataIndex < tableState.tempRows.length}
                isEditMode={tableState.canModifyRow}
                onCellChanged={tableState.onCellChanged}
                assets={props.consumables}
                inputType="combobox"
                initialDisplayText={cellProps.dataItem.consumableName}
              />
            ),
          },
          {
            header: `${localizationService.toLanguageString('custom.volume', enMessages.custom.volume)}`,
            displayField: "volumeRequirement",
            filterType: "text",
            Cell: (cellProps) => (
              <GBKendoDataCell<PointReadTableDto>
                dataFieldName="volumeRequirement"
                cellProps={cellProps}
                fieldAccessor={(x) => x.volumeRequirement === PURGE_POINT ? x.volumeRequirement="Purge" : x.volumeRequirement}
                isAddMode={cellProps.dataIndex < tableState.tempRows.length}
                isEditMode={tableState.canModifyRow}
                onCellChanged={tableState.onCellChanged}
              />
            ),
          },
          {
            header: `${localizationService.toLanguageString('custom.rfid', enMessages.custom.rfid)}`,
            displayField: "currentTagRfid",
            filterType: "text",
            Cell: (cellProps) => (
              <GBKendoDataCell<PointReadTableDto>
                dataFieldName="currentTagRfid"
                cellProps={cellProps}
                fieldAccessor={(x) => x.currentTagRfid}
                isAddMode={cellProps.dataIndex < tableState.tempRows.length}
                isEditMode={tableState.canModifyRow}
                onCellChanged={tableState.onCellChanged}
              />
            ),
          },
          {
            header: `${localizationService.toLanguageString('custom.schedule', enMessages.custom.schedule)}`,
            displayField: "scheduleName",
            Cell: (cellProps) => (
              <GBKendoDataCell<PointReadTableDto>
                dataFieldName="scheduleId"
                fieldAccessor={(x) => x.scheduleId}
                displayTextAccessor={(x) => x.name}
                cellProps={cellProps}
                isAddMode={cellProps.dataIndex < tableState.tempRows.length}
                isEditMode={tableState.canModifyRow}
                onCellChanged={tableState.onCellChanged}
                assets={props.schedules}
                inputType="combobox"
                placeholder="No Schedule"
                initialDisplayText={cellProps.dataItem.scheduleName}
              />
            ),
          },
        ],
        [
          tableState.canModifyRow,
          tableState.tempRows.length,
          props.machines,
          props.consumables,
          props.schedules,
          props.areas,
          props.points,
          filter,
          sort
        ]
      );

  const _export = React.useRef<ExcelExport | null>(null);
  // Notes are managed outside this table's query, so latestNote in the cache can be stale.
  // On export (not on every note change), fetch fresh points directly from the API — bypassing
  // react-query so the page's isFetching loader doesn't unmount this table — then patch the fresh
  // latestNote onto the already-processed rows and export that data.
  const excelExport = async () => {
    let exportData = processedData;
    try {
      const fresh = await pointsApiService.getAllPoints();
      const latestNoteById = new Map(fresh.map((p) => [p.id, p.latestNote]));
      exportData = processedData.map((row) =>
        latestNoteById.has(row.id) ? { ...row, latestNote: latestNoteById.get(row.id) } : row
      );
    } catch (e) {
      console.log(e);
    }
    _export.current?.save(exportData);
  };
  const ref = useRef<any>();

  /**The following use effects are required to fix a race condition created by updating formik fields
   * Programatically. They ensure that validation is performed after the values update instead of before.
   **/
  useEffect(() => {
    ref.current.validateForm();
  }, [ref.current?.values]);
  useEffect(() => {
    ref.current.validateForm();
  }, [ref.current]);

  /**
   * Set the skip amount to zero if the number of temp rows (indicating an add row has been added).
   * This effectively ensures the add row is always scrolled into view
   */
  useEffect(() => {
    if (tableState.tempRows.length === 1) {
      setSkip(0);
    }
  }, [tableState.tempRows.length]);

  //The following state is required for dynamic scrolling
  const [skip, setSkip] = React.useState<number>(0);
  const pageChange = (event: GridPageChangeEvent) => {
    setSkip(event.page.skip);
  };

  const isColumnForRequiredField = (displayField: string | undefined): boolean => {
    return (
      isRequiredBySchema(
        displayField?.replace("Name", "Id"), 
        PointValidationSchema({ routeOrders: props.points.map(x => x.routeOrder), localizationService, rfids: props.points.map(x => x.currentTagRfid) })
      ) &&
      (tableState.tempRows.length > 0 || tableState.canModifyRow)
    );
  };

  useEffect(() => {
    // Reset skip when filter or sort changes
    setSkip(0);
  }, [filter, sort]);

  //Data is API data + current temporary rows that have not yet been persisted using the API
  let processedData = useMemo(() => {
    let initialRows = tableState.assets;  // Always start with tableState.assets

    if (filter) {
      // First get the modified rows
      const modifiedRows = initialRows.filter(row => tableState.modifiedRowIds.includes(row.id));
      
      // Filter the non-modified rows
      const filteredRows = filterBy(
        initialRows.filter(row => !tableState.modifiedRowIds.includes(row.id)),
        filter
      );

      // Combine both sets
      initialRows = [...modifiedRows, ...filteredRows];
    }

    // Use stored order in edit mode, otherwise normal sorting
    if (tableState.canModifyRow && editModeRows.length > 0) {
      const editModeOrder = new Map(editModeRows.map((row, index) => [row.id, index]));
      initialRows.sort((a, b) => (editModeOrder.get(a.id) ?? 0) - (editModeOrder.get(b.id) ?? 0));
    } else if (sort) {
      initialRows = orderBy(initialRows, sort);
    }

    return tableState.tempRows.concat(initialRows);
  }, [tableState.assets, tableState.tempRows, tableState.modifiedRowIds, filter, sort]);

  /**
   * When machine selected, auto populate parent area to that of machine
   */
   const onMachineSelected = async (rowId: string, newValue: any, form: FormikProps<any>) => {
    const selectedMachine = props.machines.find((x: { id: any; }) => x.id === newValue);
    const selectedArea = props.areas.find((x: { id: any; }) => x.id === selectedMachine?.areaId);
    //If the field being edited is an addrow
    if (rowId == null || rowId === "") {
      tableState.setTempRows((old: Array<PointReadTableDto>) => {
        let updatedAssets = [...old];
        updatedAssets[0] = {
          ...updatedAssets[0],
          areaId: selectedArea?.id ?? "",
          areaName: selectedArea?.name ?? "",
        };
        return updatedAssets;
      });
      form.setFieldValue("areaId", selectedMachine?.areaId ?? "", false);
      await form.setFieldError("areaId", "undefined"); //No errors on selection
      return;
    }

    //Now update current machine to have same area as selected
    tableState.setAssets((old: Array<PointReadTableDto>) => {
      const modifiedRowIndex = old.findIndex((x) => x.id === rowId);
      let updatedAssets = [...old];
      updatedAssets[modifiedRowIndex] = {
        ...updatedAssets[modifiedRowIndex],
        areaId: selectedArea?.id ?? "",
        areaName: selectedArea?.name ?? "",
      };
      return updatedAssets;
    });
  };

  //Initial row added
  const initialisedRowData = {
    //Set route order to be one more than the number of points
    routeOrder: props.points.length > 0 
      ? Math.max(...props.points.map(x => x.routeOrder || 0)) + 1 
      : 1,
    name: "",
    id: "",
    pointType: "GreasingPoint",
    machineId: "",
    areaId: "",
    consumableId: "",
    volumeRequirement: 1,
    greasingTagRfid: "",
    scheduleId: null,
    status: "Active",
    consumableName: "",
    machineName: "",
    areaName: "",
    scheduleName: "",
    photo: "",
    description: "",
    crewType: "",
    criticality: "Normal",
    securityClassification: "Unclassified",
    currentTagRfid : "",
    volumeTolerance: 0,
    expanded: true
  };

  // Add this effect to handle edit mode row storage
  useEffect(() => {
    if (tableState.canModifyRow) {
        // Store current sorted rows when entering edit mode
        if (editModeRows.length === 0) {
            const sortedRows = sort ? orderBy(tableState.assets, sort) : tableState.assets;
            setEditModeRows(sortedRows);
        }
    } else {
        setEditModeRows([]);
    }
  }, [tableState.canModifyRow]);

  return (
    <div>
      <Formik
        innerRef={ref}
        key={props.points.length}
        onSubmit={async (values, { setStatus, resetForm }) => {
          if (tableState.tempRows.length > 0) {
            values.description = tableState.tempRows[0].description;
            values.routeOrder = tableState.tempRows[0].routeOrder as number;
          }

          // Check for duplicate point
          const duplicate = props.points.find(point => 
            point.name?.toLowerCase() === values.name?.toLowerCase() && 
            point.machineId === values.machineId && 
            point.areaId === values.areaId
          );

          if (duplicate) {
            toast({
              status: "error",
              title: localizationService.toLanguageString('custom.error', enMessages.custom.error),
              description: localizationService.toLanguageString('custom.pointNameExists', enMessages.custom.pointNameExists),
            });
            return;
          }

          const res = await props.createPoint(values, setStatus);
          if (res) {
            resetForm();
            tableState.setTempRows([]);
          }
        }}
        validationSchema={PointValidationSchema({ 
          routeOrders: props.points.map(x => x.routeOrder),
          localizationService, rfids: props.points.map(x => x.currentTagRfid)
        })}
        initialValues={initialisedRowData}
      >
        {({ submitForm, values, resetForm, isSubmitting }) => (
          <>
            <ExcelExport data={processedData} ref={_export} collapsible={true} fileName={localizationService.toLanguageString('custom.greaseTags', enMessages.custom.greaseTags)}>
              <ExcelExportColumn field="name" title={localizationService.toLanguageString('custom.name', enMessages.custom.name)} />
              <ExcelExportColumn field="routeOrder" title={localizationService.toLanguageString('custom.order', enMessages.custom.order)} />
              <ExcelExportColumn field="machineName" title={localizationService.toLanguageString('custom.asset', enMessages.custom.asset)} />
              <ExcelExportColumn field="areaName" title={localizationService.toLanguageString('custom.location', enMessages.custom.location)} />
              <ExcelExportColumn field="consumableName" title={localizationService.toLanguageString('custom.consumable', enMessages.custom.consumable)} />
              <ExcelExportColumn field="volumeRequirement" title={localizationService.toLanguageString('custom.volumeRequirement', enMessages.custom.volumeRequirement)} />
              <ExcelExportColumn field="currentTagRfid" title={localizationService.toLanguageString('custom.rfid', enMessages.custom.rfid)} />
              <ExcelExportColumn field="scheduleName" title={localizationService.toLanguageString('custom.schedule', enMessages.custom.schedule)} />
              <ExcelExportColumn field="latestNote" title={localizationService.toLanguageString('custom.notes', enMessages.custom.notes)} />
            </ExcelExport>
            <Grid
              key={tableState.tempRows.length.toString()}
              rowHeight={46}
              pageSize={50}
              total={processedData.length}
              data={processedData}
              expandField="expanded"
              scrollable="virtual"
              skip={skip}
              onPageChange={pageChange}
              filterable={true}
              filter={filter}
              onFilterChange={(e: GridFilterChangeEvent) => setFilter(e.filter)}
              sortable={!tableState.canModifyRow}
              sort={tableState.canModifyRow ? previousSort : sort}
              onSortChange={(e: GridSortChangeEvent) => {
                if (!tableState.canModifyRow) {
                    setSort(e.sort);
                    setPreviousSort(e.sort);
                }
              }}
              style={{
                height: props.height,
              }}
              className="w-full  z-0"
              //Custom render row,
              rowRender={(trElement: ReactElement, props: GridRowProps) => {
                let baseStyle = { ...trElement.props.style };
                if (tableState.modifiedRowIds.includes(props.dataItem.id)) {
                  baseStyle.backgroundColor = "rgba(255, 252, 88, 0.25)";
                }
                if (tableState.selectedRowsIds.includes(props.dataItem.id)) {
                  baseStyle.backgroundColor = "rgba(255, 99, 88, 0.25)";
                }

                return React.cloneElement(
                  trElement,
                  {
                    className: trElement.props.className,
                    style: baseStyle,
                    onClick: trElement.props.onClick,
                    onDoubleClick: trElement.props.onDoubleClick,
                    onContextMenu: trElement.props.onContextMenu,
                    role: trElement.props.role,
                    'aria-rowindex': trElement.props['aria-rowindex'],
                    'data-grid-row-index': trElement.props['data-grid-row-index']
                  },
                  trElement.props.children
                );
              }}
            >
              <GridToolbar>  
                <GBKendoToolbar
                  name={`${localizationService.toLanguageString('custom.tagMapping', enMessages.custom.tagMapping)}`}
                  tableState={tableState}
                  exportAction={excelExport}
                  importAction={null}
                  addAction={
                    props.createPoint
                      ? () => {
                        if (tableState.tempRows.length === 1) {
                          tableState.setTempRows([]);
                          return;
                        }
                        tableState.setTempRows([...tableState.tempRows, initialisedRowData]);
                        }
                      : null
                  }
                  updateAction={
                    props.updatePoints
                      ? async () => {
                          let updatedAssets = tableState.assets.filter((x) => tableState.modifiedRowIds.includes(x.id));
                          await props.updatePoints(updatedAssets);
                        }
                      : null
                  }
                  deleteAction={
                    props.deletePoints ? async () => await props.deletePoints(tableState.selectedRowsIds) : null
                  }
                  discardChanges={() => tableState.resetState()}
                />
              </GridToolbar>
             
              {/* Selection - only if in edit mode*/}
              {tableState.canModifyRow && (
                <Column  
                  key={"Selection"}
                  cell={(props) => (
                    (props.rowType === 'groupHeader' ? 
                    null :
                    <td>  
                        <Checkbox
                          onChange={() => {
                            if (tableState.selectedRowsIds.includes(props.dataItem.id)) {
                              tableState.setSelectedRowIds(
                                tableState.selectedRowsIds.filter((id) => id !== props.dataItem?.id)
                              );
                            } else {
                              tableState.setSelectedRowIds([...tableState.selectedRowsIds, props.dataItem?.id]);
                            }
                          }}
                          checked={tableState.selectedRowsIds.includes(props.dataItem.id)}
                        />
                    </td>)
                  )}
                  width="50px"
                  filterable={false}
                  sortable={false} />) 
                }
              

               {/* From Column Array */}
               {columns.map((column, index) => {
                if(column.displayField === "description") {
                  return (
                    //Create wrapper/HOC with editable cell and add cell definitions,
                    <Column
                      width="65px"
                      className="text-center"
                      key={index}
                      cell={column.Cell}
                      title={`${column.header}${isColumnForRequiredField(column.displayField) ? "*" : ""}`}
                      field={column.displayField}
                      filterable={false}
                      sortable={true}
                    />
                  );
                }else {
                  return (
                    //Create wrapper/HOC with editable cell and add cell definitions,
                    <Column
                      width="auto"
                      key={index}
                      cell={column.Cell}
                      // Show asterix in header if required TODO: pretty hacky - assumes that the name of the data field is the same as the display field with Name replaced.
                      title={`${column.header}${isColumnForRequiredField(column.displayField) ? "*" : ""}`}
                      field={column.displayField}
                      filter={column.filterType}
                      filterCell={column.filterType === "date" ? CustomDateFilterCell : CustomTextFilterCell}
                    />
                  );
                }
              })}

              {/* Inline Action options (If ActionDefinitions are provided) */}
              {props.extraInlineActions != null && props.extraInlineActions.length > 0 && (
                <Column
                  title={localizationService.toLanguageString('custom.actions', enMessages.custom.actions)}
                  sortable={false}
                  filterable={false}
                  cell={(cellProps) => {
                    return (
                      <td className="flex flex-wrap justify-around gap-1">
                        {tableState.tempRows.length <= cellProps.dataIndex &&
                          props.extraInlineActions.map((action: any) => {
                            return (
                              <Button
                                className=""
                                themeColor={action.themeColor}
                                onClick={() => action.onClick(cellProps.dataItem)}
                              >
                                {action.text}
                              </Button>
                            );
                          })}
                      </td>
                    );
                  }}
                />
              )}

              {/* Command Cell (For add rows only)*/}
              {tableState.tempRows.length > 0 && (
                <Column
                  width="150px"
                  sortable={false}
                  filterable={false}
                  key={"Commands"}
                  cell={(props) => // Add Button for new assets
                  (props.rowType === 'groupHeader' ?
                    null :
                    (
                      <td>
                        {props.dataIndex === 0 && (
                          <div className="flex space-x-1">
                            <Button onClick={submitForm} disabled={isSubmitting} themeColor={"primary"}>
                            {localizationService.toLanguageString('custom.add', enMessages.custom.add)}
                            </Button>
                            <Button
                              onClick={() => {
                                tableState.setTempRows([]);
                                resetForm();
                              } }
                              themeColor={"base"}
                            >
                              {localizationService.toLanguageString('custom.exit', enMessages.custom.exit)}
                            </Button>
                          </div>
                        )}
                      </td>)
                  )}
                />
              )}
            </Grid>
          </>
          )}
      </Formik>
    </div>
  );
};

export default PointTable;